import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { channelPartnerApi, toApiAbsoluteUrl } from "../../api/client";
import {
  Users,
  UserPlus,
  AlertCircle,
  CheckCircle,
  Clock,
  X,
  FileText,
} from "lucide-react";

const toAbsoluteUrl = toApiAbsoluteUrl;

function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  if (!bytes || Number.isNaN(bytes)) return "-";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${sizes[i]}`;
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

const ChannelPartnerHome = () => {
  const [dashboardData, setDashboardData] = useState({
    totalPromoters: 0,
    activePromoters: 0,
    pendingPromoters: 0,
    totalRevenue: 0,
    totalProviders: 0,
    totalListings: 0,
    recentPromoters: [],
    statistics: {},
  });
  const [loading, setLoading] = useState(true);
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [verificationModal, setVerificationModal] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState("");

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get dashboard stats
      const dashboardData = await channelPartnerApi.getDashboard();

      // Get complete promoters data using the same API as Promoter.jsx
      const promotersData = await channelPartnerApi.listPromoters({
        page: 1,
        limit: 50, // Get more promoters for dashboard
      });

      // Get promoters array from response (could be 'promoters' or 'items')
      const promotersArray =
        promotersData?.promoters || promotersData?.items || [];

      // Map backend response to frontend expected structure
      const mappedData = {
        totalPromoters:
          dashboardData.statistics?.totalPromoters ||
          promotersData?.pagination?.total ||
          promotersData?.total ||
          promotersArray.length ||
          0,
        activePromoters:
          dashboardData.statistics?.activePromoters ||
          dashboardData.statistics?.approvedPromoters ||
          promotersArray.filter((p) => {
            const status = p.status || p.promoterProfile?.status;
            return status === "approved";
          }).length ||
          0,
        pendingPromoters:
          dashboardData.statistics?.pendingPromoters ||
          promotersArray.filter((p) => {
            const status = p.status || p.promoterProfile?.status;
            return (
              status === "pending_verification" || status === "pending_approval"
            );
          }).length ||
          0,
        totalRevenue: dashboardData.statistics?.totalRevenue || 0,
        recentPromoters: promotersArray.slice(0, 5) || [],
        promoters: promotersArray,
        statistics: dashboardData.statistics || {},
        profile: dashboardData.profile || {},
      };

      // Calculate stats directly from promoters array and store in state
      if (mappedData.promoters && mappedData.promoters.length > 0) {
        const totalProviders = mappedData.promoters.reduce((sum, p) => {
          const count =
            p.verifiedProvidersCount ??
            p.totalProvidersCreated ??
            p.promoterProfile?.totalProvidersCreated ??
            0;
          return sum + (Number(count) || 0);
        }, 0);

        const totalListings = mappedData.promoters.reduce((sum, p) => {
          const count =
            p.totalListingsUnderPromoter ??
            p.promoterProfile?.totalListingsUnderPromoter ??
            0;
          return sum + (Number(count) || 0);
        }, 0);

        // Store calculated totals in mappedData for direct access
        mappedData.totalProviders = totalProviders;
        mappedData.totalListings = totalListings;
      }

      setDashboardData(mappedData);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      // Set default data on error
      setDashboardData({
        totalPromoters: 0,
        activePromoters: 0,
        pendingPromoters: 0,
        totalRevenue: 0,
        totalProviders: 0,
        totalListings: 0,
        recentPromoters: [],
        promoters: [],
        statistics: {},
        profile: {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPromoter = (promoter) => {
    setSelectedPromoter(promoter);
    setVerificationModal(true);
    setVerificationNotes("");
  };

  const confirmVerification = async (isVerified) => {
    if (!selectedPromoter) return;

    // Try different possible ID fields
    const promoterId =
      selectedPromoter.promoterProfileId ||
      selectedPromoter._id ||
      selectedPromoter.id ||
      selectedPromoter.promoterId;

    if (!promoterId) {
      console.error("No promoter ID found in:", selectedPromoter);
      alert(
        "Error: Promoter profile ID not found. Please refresh the page and try again."
      );
      return;
    }

    try {
      setVerificationLoading(true);

      await channelPartnerApi.verifyPromoter(
        promoterId,
        isVerified,
        verificationNotes
      );

      // Reload dashboard data to reflect changes
      await loadDashboardData();

      // Close modal
      setVerificationModal(false);
      setSelectedPromoter(null);
      setVerificationNotes("");
    } catch (error) {
      console.error("Failed to verify promoter:", error);
      alert("Failed to verify promoter. Please try again.");
    } finally {
      setVerificationLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "text-green-600 bg-green-100";
      case "pending_approval":
        return "text-yellow-600 bg-yellow-100";
      case "pending_verification":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "pending_approval":
        return <Clock className="h-4 w-4" />;
      case "pending_verification":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
        Channel Partner Dashboard
      </h2>

      {/* Welcome card with comprehensive stats */}
      <div className="bg-white shadow-xl rounded-xl p-8 border border-gray-200 w-full max-w-6xl mx-auto mb-5">
        {/* Main Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
          {/* Promoters */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-3xl font-bold text-yellow-600">
              {dashboardData.activePromoters}
            </div>
            <div className="text-sm text-gray-600 mb-1">Promoters Active</div>
            <div className="text-xs text-red-600 font-medium">
              {dashboardData.promoters?.filter(
                (p) => p.status === "pending_verification"
              ).length ||
                dashboardData.pendingPromoters ||
                0}{" "}
              pending
            </div>
          </div>
          {/* Providers Created */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-3xl font-bold text-green-600">
              {dashboardData.totalProviders !== undefined
                ? dashboardData.totalProviders
                : (() => {
                    if (
                      !dashboardData.promoters ||
                      dashboardData.promoters.length === 0
                    )
                      return 0;
                    return dashboardData.promoters.reduce((sum, p) => {
                      // Prefer verifiedProvidersCount, fallback to totalProvidersCreated
                      const count =
                        p.verifiedProvidersCount !== undefined &&
                        p.verifiedProvidersCount !== null
                          ? Number(p.verifiedProvidersCount)
                          : p.totalProvidersCreated !== undefined &&
                            p.totalProvidersCreated !== null
                          ? Number(p.totalProvidersCreated)
                          : Number(
                              p.promoterProfile?.totalProvidersCreated || 0
                            );
                      return sum + (isNaN(count) ? 0 : count);
                    }, 0);
                  })()}
            </div>
            <div className="text-sm text-gray-600 mb-1">Providers Created</div>
            <div className="text-xs text-gray-500">By all promoters</div>
          </div>
          {/* Listings Created */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-3xl font-bold text-purple-600">
              {dashboardData.totalListings !== undefined
                ? dashboardData.totalListings
                : (() => {
                    if (
                      !dashboardData.promoters ||
                      dashboardData.promoters.length === 0
                    )
                      return 0;
                    return dashboardData.promoters.reduce((sum, p) => {
                      const count =
                        p.totalListingsUnderPromoter !== undefined &&
                        p.totalListingsUnderPromoter !== null
                          ? Number(p.totalListingsUnderPromoter)
                          : Number(
                              p.promoterProfile?.totalListingsUnderPromoter || 0
                            );
                      return sum + (isNaN(count) ? 0 : count);
                    }, 0);
                  })()}
            </div>
            <div className="text-sm text-gray-600 mb-1">Listings Created</div>
            <div className="text-xs text-gray-500">By all promoters</div>
          </div>
          {/* Revenue */}
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <div className="text-3xl font-bold text-indigo-600">
              {formatCurrency(dashboardData.totalRevenue)}
            </div>
            <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
            <div className="text-xs text-gray-500">
              Total of all under network
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {/* <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/channelpartner/create-promoter"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <UserPlus className="h-8 w-8 text-blue-600 mr-4" />
            <div>
              <h3 className="font-medium text-gray-900">Create New Promoter</h3>
              <p className="text-sm text-gray-500">
                Add a new promoter to your network
              </p>
            </div>
          </Link>

          <Link
            to="/channelpartner/promoters"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Users className="h-8 w-8 text-green-600 mr-4" />
            <div>
              <h3 className="font-medium text-gray-900">Manage Promoters</h3>
              <p className="text-sm text-gray-500">
                View and manage your promoter network
              </p>
            </div>
          </Link>
        </div>
      </div> */}

      {/* Promoters Performance & Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Promoters Performance & Activity
          </h2>
          <Link
            to="/channelpartner/promoters"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            View all
          </Link>
        </div>

        {dashboardData.promoters?.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promoter
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Providers
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Listings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData.promoters
                    .sort((a, b) => {
                      // Sort by number of verified providers (descending)
                      const aProviders =
                        a.verifiedProvidersCount ||
                        a.totalProvidersCreated ||
                        a.promoterProfile?.totalProvidersCreated ||
                        0;
                      const bProviders =
                        b.verifiedProvidersCount ||
                        b.totalProvidersCreated ||
                        b.promoterProfile?.totalProvidersCreated ||
                        0;
                      return bProviders - aProviders; // Descending order
                    })
                    .map((promoter) => {
                      // Get counts and revenue - all from packageAmount of providers under this promoter
                      // Try multiple paths to ensure we get the correct values
                      const providersCount =
                        promoter.verifiedProvidersCount !== undefined
                          ? promoter.verifiedProvidersCount
                          : promoter.totalProvidersCreated ||
                            promoter.promoterProfile?.totalProvidersCreated ||
                            0;
                      const listingsCount =
                        promoter.totalListingsUnderPromoter ||
                        promoter.promoterProfile?.totalListingsUnderPromoter ||
                        0;
                      const status =
                        promoter.status ||
                        promoter.promoterProfile?.status ||
                        "unknown";
                      // Revenue: sum of packageAmount from VERIFIED providers created by this promoter
                      const revenue =
                        promoter.revenue || promoter.totalRevenue || 0;

                      // Debug log removed for production - uncomment if needed
                      // console.log(`Promoter ${promoter.name || promoter.email} stats:`, {
                      //   providersCount,
                      //   listingsCount,
                      //   revenue,
                      //   verifiedProvidersCount: promoter.verifiedProvidersCount,
                      //   totalProvidersCreated: promoter.totalProvidersCreated,
                      //   promoterProfile: promoter.promoterProfile?.totalProvidersCreated,
                      // });

                      return (
                        <tr
                          key={promoter.id || promoter._id}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {promoter.name || "-"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {promoter.email || "-"}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                status
                              )}`}
                            >
                              {getStatusIcon(status)}
                              <span className="ml-1">
                                {(status || "pending")?.replace(/_/g, " ")}
                              </span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                promoter.paymentStatus === "verified"
                                  ? "text-green-600 bg-green-100"
                                  : promoter.paymentStatus === "pending"
                                  ? "text-yellow-600 bg-yellow-100"
                                  : "text-gray-600 bg-gray-100"
                              }`}
                            >
                              {promoter.paymentStatus || "pending"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <span
                                className={`text-lg font-bold ${
                                  providersCount > 0
                                    ? "text-green-600"
                                    : "text-gray-400"
                                }`}
                              >
                                {providersCount}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <span
                                className={`text-lg font-bold ${
                                  listingsCount > 0
                                    ? "text-blue-600"
                                    : "text-gray-400"
                                }`}
                              >
                                {listingsCount}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span
                                className={`text-base font-semibold ${
                                  revenue > 0
                                    ? "text-green-600"
                                    : "text-gray-400"
                                }`}
                              >
                                {formatCurrency(revenue)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {promoter.createdAt
                              ? new Date(
                                  promoter.createdAt
                                ).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={async () => {
                                  try {
                                    const promoterId =
                                      promoter.promoterProfileId ||
                                      promoter._id ||
                                      promoter.id;
                                    try {
                                      const detailedResponse =
                                        await channelPartnerApi.getPromoterDetails(
                                          promoterId
                                        );
                                      const detailedData =
                                        detailedResponse.data ||
                                        detailedResponse;
                                      if (
                                        detailedData &&
                                        (detailedData.name ||
                                          detailedData.email)
                                      ) {
                                        setSelectedRecord(detailedData);
                                        return;
                                      }
                                    } catch (detailError) {
                                      // Failed to get detailed promoter data, using basic data
                                    }
                                    setSelectedRecord(promoter);
                                  } catch (error) {
                                    console.error("Error:", error);
                                    setSelectedRecord(promoter);
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-900 font-medium"
                              >
                                View
                              </button>
                              {status === "pending_verification" && (
                                <button
                                  onClick={() => handleVerifyPromoter(promoter)}
                                  className="text-green-600 hover:text-green-900 font-medium"
                                >
                                  Verify
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Summary Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {(() => {
                    if (
                      !dashboardData.promoters ||
                      dashboardData.promoters.length === 0
                    )
                      return 0;
                    return dashboardData.promoters.reduce((sum, p) => {
                      const count =
                        p.verifiedProvidersCount !== undefined &&
                        p.verifiedProvidersCount !== null
                          ? p.verifiedProvidersCount
                          : p.totalProvidersCreated !== undefined &&
                            p.totalProvidersCreated !== null
                          ? p.totalProvidersCreated
                          : p.promoterProfile?.totalProvidersCreated || 0;
                      return sum + (count || 0);
                    }, 0);
                  })()}
                </div>
                <div className="text-sm text-gray-500">
                  Total Verified Providers
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {(() => {
                    if (
                      !dashboardData.promoters ||
                      dashboardData.promoters.length === 0
                    )
                      return 0;
                    return dashboardData.promoters.reduce((sum, p) => {
                      const count =
                        p.totalListingsUnderPromoter !== undefined &&
                        p.totalListingsUnderPromoter !== null
                          ? p.totalListingsUnderPromoter
                          : p.promoterProfile?.totalListingsUnderPromoter || 0;
                      return sum + (count || 0);
                    }, 0);
                  })()}
                </div>
                <div className="text-sm text-gray-500">Total Listings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    (() => {
                      if (
                        !dashboardData.promoters ||
                        dashboardData.promoters.length === 0
                      )
                        return 0;
                      return dashboardData.promoters.reduce((sum, p) => {
                        const rev =
                          p.revenue !== undefined && p.revenue !== null
                            ? p.revenue
                            : p.totalRevenue !== undefined &&
                              p.totalRevenue !== null
                            ? p.totalRevenue
                            : 0;
                        return sum + (rev || 0);
                      }, 0);
                    })()
                  )}
                </div>
                <div className="text-sm text-gray-500">Total Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {(() => {
                    if (
                      !dashboardData.promoters ||
                      dashboardData.promoters.length === 0
                    )
                      return 0;
                    return dashboardData.promoters.filter((p) => {
                      const count =
                        p.verifiedProvidersCount !== undefined &&
                        p.verifiedProvidersCount !== null
                          ? p.verifiedProvidersCount
                          : p.totalProvidersCreated !== undefined &&
                            p.totalProvidersCreated !== null
                          ? p.totalProvidersCreated
                          : p.promoterProfile?.totalProvidersCreated || 0;
                      return count > 0;
                    }).length;
                  })()}
                </div>
                <div className="text-sm text-gray-500">Active Promoters</div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No promoters yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first promoter.
            </p>
            <div className="mt-6">
              <Link
                to="/channelpartner/create-promoter"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create Promoter
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Promoters Pending Verification */}
      {dashboardData.promoters?.filter((p) => {
        const status = p.status || p.promoterProfile?.status;
        return status === "pending_verification";
      }).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              Promoters Pending Verification
            </h2>
            <Link
              to="/channelpartner/promoters"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View all
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.promoters
                  .filter((p) => {
                    const status = p.status || p.promoterProfile?.status;
                    return status === "pending_verification";
                  })
                  .map((promoter) => (
                    <tr key={promoter.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {promoter.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {promoter.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            promoter.paymentStatus === "verified"
                              ? "text-green-600 bg-green-100"
                              : "text-yellow-600 bg-yellow-100"
                          }`}
                        >
                          {promoter.paymentStatus || "pending"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(promoter.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={async () => {
                              try {
                                // Try to get complete promoter details with documents
                                const promoterId =
                                  promoter.promoterProfileId ||
                                  promoter._id ||
                                  promoter.id;

                                // First try to get detailed promoter data
                                try {
                                  const detailedResponse =
                                    await channelPartnerApi.getPromoterDetails(
                                      promoterId
                                    );
                                  const detailedData =
                                    detailedResponse.data || detailedResponse;

                                  if (
                                    detailedData &&
                                    (detailedData.name || detailedData.email)
                                  ) {
                                    setSelectedRecord(detailedData);
                                    return;
                                  }
                                } catch (detailError) {
                                  // Failed to get detailed data, using basic data
                                }

                                // Fallback to basic promoter data
                                setSelectedRecord(promoter);
                              } catch (error) {
                                console.error(
                                  "Error setting promoter data:",
                                  error
                                );
                                setSelectedRecord(promoter);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleVerifyPromoter(promoter)}
                            className="text-green-600 hover:text-green-900 font-medium"
                          >
                            Verify
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Promoter Details</h3>
              <button
                onClick={() => setSelectedRecord(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-blue-800">
                  Personal Information
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Name:</strong> {selectedRecord.name || "-"}
                  </p>
                  <p>
                    <strong>Father's Name:</strong>{" "}
                    {selectedRecord.fatherName || "-"}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedRecord.email || "-"}
                  </p>
                  <p>
                    <strong>Phone:</strong>{" "}
                    {selectedRecord?.userId?.phoneNumber ||
                      selectedRecord.phoneNumber ||
                      "-"}
                  </p>
                  <p>
                    <strong>Blood Group:</strong>{" "}
                    {selectedRecord.bloodGroup || "-"}
                  </p>
                  <p>
                    <strong>Qualification:</strong>{" "}
                    {selectedRecord.qualification || "-"}
                  </p>
                  <p>
                    <strong>PAN Number:</strong>{" "}
                    {selectedRecord.panNumber || "-"}
                  </p>
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-green-800">
                  Address
                </h4>
                {selectedRecord.address ? (
                  <div className="space-y-2 text-sm">
                    <p>
                      <strong>Street:</strong>{" "}
                      {selectedRecord.address.street || "-"}
                    </p>
                    <p>
                      <strong>City:</strong>{" "}
                      {selectedRecord.address.city || "-"}
                    </p>
                    <p>
                      <strong>State:</strong>{" "}
                      {selectedRecord.address.state || "-"}
                    </p>
                    <p>
                      <strong>Pincode:</strong>{" "}
                      {selectedRecord.address.pincode || "-"}
                    </p>
                    <p>
                      <strong>Country:</strong>{" "}
                      {selectedRecord.address.country || "-"}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">No address provided</p>
                )}
              </div>

              {/* Status & Payment Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-purple-800">
                  Status & Payment
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Profile Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        selectedRecord.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : selectedRecord.status === "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : selectedRecord.status === "pending_verification"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {selectedRecord.status?.replace(/_/g, " ") || "-"}
                    </span>
                  </p>
                  <p>
                    <strong>Payment Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        selectedRecord?.userId?.paymentStatus === "verified"
                          ? "bg-green-100 text-green-800"
                          : selectedRecord?.userId?.paymentStatus === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {selectedRecord?.userId?.paymentStatus ||
                        selectedRecord?.paymentDetails?.paymentStatus ||
                        "-"}
                    </span>
                  </p>
                  <p>
                    <strong>Payment Amount:</strong>{" "}
                    {selectedRecord?.paymentDetails?.amount
                      ? `₹${selectedRecord.paymentDetails.amount}`
                      : "-"}
                  </p>
                  <p>
                    <strong>UTR ID:</strong>{" "}
                    {selectedRecord?.paymentDetails?.utrId ||
                      selectedRecord?.userId?.utrId ||
                      "-"}
                  </p>
                  <p>
                    <strong>Commission Rate:</strong>{" "}
                    {selectedRecord.commissionRate
                      ? `${selectedRecord.commissionRate}%`
                      : "-"}
                  </p>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-3 text-orange-800">
                  Statistics
                </h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Providers Created:</strong>{" "}
                    {selectedRecord.totalProvidersCreated || 0}
                  </p>
                  <p>
                    <strong>Listings Under Promoter:</strong>{" "}
                    {selectedRecord.totalListingsUnderPromoter || 0}
                  </p>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {selectedRecord.createdAt
                      ? formatDate(selectedRecord.createdAt)
                      : "-"}
                  </p>
                  <p>
                    <strong>Verified At:</strong>{" "}
                    {selectedRecord.verifiedAt
                      ? formatDate(selectedRecord.verifiedAt)
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mt-6">
              <h4 className="font-semibold text-lg mb-3 text-red-800">
                Documents (
                {(() => {
                  const docs = selectedRecord.documents;
                  if (Array.isArray(docs)) {
                    return docs.length;
                  } else if (docs && typeof docs === "object") {
                    return Object.keys(docs).length;
                  }
                  return 0;
                })()}
                )
              </h4>
              <div className="mt-3 max-h-72 overflow-y-auto border rounded">
                {(() => {
                  const docs = selectedRecord.documents;
                  const hasDocuments = Array.isArray(docs)
                    ? docs.length > 0
                    : docs &&
                      typeof docs === "object" &&
                      Object.keys(docs).length > 0;

                  if (!hasDocuments) {
                    return (
                      <div className="p-4 text-sm text-gray-500">
                        No documents uploaded
                      </div>
                    );
                  }

                  // Handle array format (full document objects)
                  if (Array.isArray(docs)) {
                    return (
                      <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 border">Preview</th>
                            <th className="p-2 border">Name</th>
                            <th className="p-2 border">Type</th>
                            <th className="p-2 border">Size</th>
                            <th className="p-2 border">Uploaded</th>
                            <th className="p-2 border">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => {
                            const isImage = (d?.mimeType || "").startsWith(
                              "image/"
                            );
                            return (
                              <tr key={d.id}>
                                <td className="p-2 border align-middle">
                                  {isImage ? (
                                    <button
                                      className="inline-flex items-center"
                                      onClick={() =>
                                        window.open(
                                          toAbsoluteUrl(d.url),
                                          "_blank",
                                          "noopener"
                                        )
                                      }
                                      title="Open image"
                                    >
                                      <img
                                        src={toAbsoluteUrl(d.url)}
                                        alt={
                                          d.originalName || d.kind || "document"
                                        }
                                        className="h-12 w-12 object-cover rounded"
                                      />
                                    </button>
                                  ) : (
                                    <div className="flex items-center text-gray-600">
                                      <FileText size={18} className="mr-1" />
                                      <span>File</span>
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.originalName || d.kind || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.mimeType || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {formatBytes(d.size)}
                                </td>
                                <td className="p-2 border align-middle">
                                  {formatDate(d.uploadedAt)}
                                </td>
                                <td className="p-2 border align-middle">
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                      onClick={() =>
                                        window.open(
                                          toAbsoluteUrl(d.url),
                                          "_blank",
                                          "noopener"
                                        )
                                      }
                                    >
                                      Open
                                    </button>
                                    <a
                                      className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                      href={toAbsoluteUrl(d.url)}
                                      download={d.originalName || true}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Download
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  }

                  // Handle object format (document IDs)
                  if (docs && typeof docs === "object") {
                    const documentTypes = {
                      experienceDocuments: "Experience Documents",
                      panCardImage: "PAN Card",
                      kycDocument: "KYC Document",
                      kycImage: "KYC Image",
                      cvUpload: "CV Upload",
                      photoUpload: "Photo Upload",
                    };

                    return (
                      <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 border">Document Type</th>
                            <th className="p-2 border">Document ID</th>
                            <th className="p-2 border">Status</th>
                            <th className="p-2 border">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(docs).map(([key, docId]) => (
                            <tr key={key}>
                              <td className="p-2 border align-middle">
                                <div className="flex items-center text-gray-600">
                                  <FileText size={18} className="mr-2" />
                                  <span className="font-medium">
                                    {documentTypes[key] || key}
                                  </span>
                                </div>
                              </td>
                              <td className="p-2 border align-middle text-sm">
                                {docId}
                              </td>
                              <td className="p-2 border align-middle">
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  Uploaded
                                </span>
                              </td>
                              <td className="p-2 border align-middle">
                                <div className="flex items-center gap-2">
                                  <button
                                    className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                    onClick={() =>
                                      window.open(
                                        toAbsoluteUrl(`/uploads/${docId}`),
                                        "_blank",
                                        "noopener"
                                      )
                                    }
                                  >
                                    View
                                  </button>
                                  <a
                                    className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                    href={toAbsoluteUrl(`/uploads/${docId}`)}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }

                  return (
                    <div className="p-4 text-sm text-gray-500">
                      No documents uploaded
                    </div>
                  );
                })()}
              </div>
            </div>
            <button
              onClick={() => setSelectedRecord(null)}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {verificationModal && selectedPromoter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Verify Promoter
              </h3>
              <button
                onClick={() => setVerificationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Promoter:</strong> {selectedPromoter.name}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Email:</strong> {selectedPromoter.email}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Payment Status:</strong>{" "}
                {selectedPromoter.paymentStatus || "pending"}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Notes (Optional)
              </label>
              <textarea
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="Add any notes about this verification..."
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => confirmVerification(true)}
                disabled={verificationLoading}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verificationLoading ? "Verifying..." : "✓ Approve"}
              </button>
              <button
                onClick={() => confirmVerification(false)}
                disabled={verificationLoading}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verificationLoading ? "Verifying..." : "✗ Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelPartnerHome;
